// src/components/CryptoStore.jsx

import { motion } from 'framer-motion';
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useRef, useState } from 'react';
import { FaChartLine, FaCoins } from 'react-icons/fa';
import { TiArrowUp } from "react-icons/ti";
import { useNavigate } from 'react-router-dom';
import AnimatedTitle from './AnimatedTitle';
import Footer from './Footer';
import ScrollDownButton from './ScrollDownButton';
import TokenCard from './TokenCard';
import GlassButton from './ui/GlassButton';
import { SparklesCore } from './ui/SparklesCore';

gsap.registerPlugin(ScrollTrigger);

const tokens = [    
  {
      id: 1,
      name: 'Zero-Gecko',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Psychedelic rock gecko, a critically endangered lisard.',
      creation: 'Appeared around 50 million years ago.',
      extinction: 'Critically endangered due to habitat loss.',
      image: '1-GECKO-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£10.00'
  },
  {
      id: 2,
      name: 'Zero-Tiger',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Tigers are large cats known for their stripes.',
      creation: 'Appeared around 2 million years ago.',
      extinction: 'Critically endangered with several subspecies extinct.',
      image: '2-TIGER-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£20.00'
  },
  {
      id: 3,
      name: 'Zero-Rhino',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Rhinos are large herbivores with characteristic horns.',
      creation: 'Appeared in the Eocene epoch, around 50 million years ago.',
      extinction: 'Several species are critically endangered.',
      image: '3-RHINO-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£30.00'
  },
  {
      id: 4,
      name: 'Zero-Stego',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Stegosaurus was a herbivorous dinosaur with distinctive back plates.',
      creation: 'Lived during the Late Jurassic period.',
      extinction: 'Extinct around 150 million years ago.',
      image: '4-STEGO-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£40.00'
  },
  {
      id: 5,
      name: 'Zero-Cheeto',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Cheetahs are the fastest land animals.',
      creation: 'Appeared around 4 million years ago.',
      extinction: 'Endangered due to habitat loss.',
      image: '5-CHEETO-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£50.00'
  },
  {
      id: 6,
      name: 'Zero-Bison',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Bison are large bovines native to North America and Europe.',
      creation: 'Appeared around 2 million years ago.',
      extinction: 'American bison were near extinction but are recovering.',
      image: '6-BISON-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£60.00'
  },
  {
      id: 7,
      name: 'Zero-Dino',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'General term for various dinosaurs.',
      creation: 'Appeared in the Mesozoic era.',
      extinction: 'Most became extinct 65 million years ago.',
      image: '7-DINO-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£70.00'
  },
  {
      id: 8,
      name: 'Zero-Moa',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Moas were large, flightless birds from New Zealand.',
      creation: 'Appeared in the Miocene epoch.',
      extinction: 'Became extinct around 1400 AD.',
      image: '8-MOA-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£80.00'
  },
  {
      id: 9,
      name: 'Zero-Woolly',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Woolly mammoths were large, hairy elephants.',
      creation: 'Appeared around 400,000 years ago.',
      extinction: 'Became extinct around 4,000 years ago.',
      image: '9-WOOLY-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£90.00'
  },
  {
      id: 10,
      name: 'Zero-Sabre',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Sabre-toothed cats were known for their long, curved canine teeth.',
      creation: 'Appeared around 2.5 million years ago.',
      extinction: 'Became extinct around 10,000 years ago.',
      image: '10-SABRE-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£100.00'
  },
  {
      id: 11,
      name: 'Zero-Quagga',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Quaggas were a subspecies of plains zebra.',
      creation: 'Appeared around 4 million years ago.',
      extinction: 'Became extinct in the late 19th century.',
      image: '11-QUAGGA-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£110.00'
  },
  {
      id: 12,
      name: 'Zero-Panda',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Giant pandas are bears native to China.',
      creation: 'Appeared around 2-3 million years ago.',
      extinction: 'Vulnerable due to habitat loss.',
      image: '12-PANDA-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£120.00'
  },
  {
      id: 13,
      name: 'Zero-Pteran',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Pteranodon was a large flying reptile.',
      creation: 'Lived during the Late Cretaceous period.',
      extinction: 'Became extinct 65 million years ago.',
      image: '13-PTERAN-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£130.00'
  },
  {
      id: 14,
      name: 'Zero-Thylac',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Thylacine, also known as the Tasmanian tiger.',
      creation: 'Appeared around 4 million years ago.',
      extinction: 'Became extinct in the 20th century.',
      image: '14-THYLAC-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£140.00'
  },
  {
      id: 15,
      name: 'Zero-Auroch',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Aurochs were large wild cattle.',
      creation: 'Appeared around 2 million years ago.',
      extinction: 'Became extinct in the 17th century.',
      image: '15-AUROCH-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£150.00'
  },
  {
      id: 16,
      name: 'Zero-Ivory',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Ivory-billed woodpecker, a large woodpecker species.',
      creation: 'Appeared in the Pleistocene epoch.',
      extinction: 'Critically endangered, possibly extinct.',
      image: '16-IVORY-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£160.00'
  },
  {
      id: 17,
      name: 'Zero-Pang',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Pangolins are scaly anteaters.',
      creation: 'Appeared around 55 million years ago.',
      extinction: 'Critically endangered due to poaching.',
      image: '17-PANG-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£170.00'
  },
  {
      id: 18,
      name: 'Zero-Condor',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'California condors are large vultures.',
      creation: 'Appeared around 40 million years ago.',
      extinction: 'Critically endangered but conservation efforts are ongoing.',
      image: '18-CONDOR-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£180.00'
  },
  {
      id: 19,
      name: 'Zero-Baiji',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Baiji, or Yangtze river dolphin.',
      creation: 'Appeared around 25 million years ago.',
      extinction: 'Declared functionally extinct in 2006.',
      image: '19-BAIJI-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£190.00'
  },
  {
      id: 20,
      name: 'Zero-Glypto',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Glyptodon was a large, armored mammal.',
      creation: 'Lived during the Pleistocene epoch.',
      extinction: 'Became extinct around 10,000 years ago.',
      image: '20-GLYPTO-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£200.00'
  },
  {
      id: 21,
      name: 'Zero-Megal',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Megalodon was a giant prehistoric shark.',
      creation: 'Lived during the Cenozoic era.',
      extinction: 'Became extinct around 2.6 million years ago.',
      image: '21-MEGAL-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£210.00'
  },
  {
      id: 22,
      name: 'Zero-Dire',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Dire wolves were large prehistoric canines.',
      creation: 'Lived during the Late Pleistocene epoch.',
      extinction: 'Became extinct around 10,000 years ago.',
      image: '22-DIRE-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£220.00'
  },
  {
      id: 23,
      name: 'Zero-Quetzal',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Quetzalcoatlus was a large pterosaur.',
      creation: 'Lived during the Late Cretaceous period.',
      extinction: 'Became extinct 65 million years ago.',
      image: '23-QUETZAL-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£230.00'
  },
  {
      id: 24,
      name: 'Zero-Lynx',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Iberian lynx, a wild cat species.',
      creation: 'Appeared around 5 million years ago.',
      extinction: 'Critically endangered due to habitat loss.',
      image: '24-LYNX-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£240.00'
  },
  {
      id: 25,
      name: 'Zero-Sauro',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Brachiosaurus, a large herbivorous dinosaur.',
      creation: 'Lived during the Late Jurassic period.',
      extinction: 'Became extinct around 150 million years ago.',
      image: '25-SAURO-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£250.00'
  },
  {
      id: 26,
      name: 'Zero-Okapi',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Okapi, a relative of the giraffe.',
      creation: 'Appeared around 16 million years ago.',
      extinction: 'Endangered due to habitat loss.',
      image: '26-OKAPI-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£260.00'
  },
  {
      id: 27,
      name: 'Zero-Kakapo',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Kakapo, a large flightless parrot from New Zealand.',
      creation: 'Appeared around 25 million years ago.',
      extinction: 'Critically endangered due to predation and habitat loss.',
      image: '27-KAKAPO-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£270.00'
  },
  {
      id: 28,
      name: 'Zero-Vaquita',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Vaquita, a small porpoise.',
      creation: 'Appeared around 2 million years ago.',
      extinction: 'Critically endangered with very few individuals left.',
      image: '28-VAQUITA-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£280.00'
  },
  {
      id: 29,
      name: 'Zero-Gorilla',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Mountain gorillas are large primates.',
      creation: 'Appeared around 2 million years ago.',
      extinction: 'Critically endangered due to poaching and habitat loss.',
      image: '29-GORILLA-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£290.00'
  },
  {
      id: 30,
      name: 'Zero-RedWolf',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Red wolf, a North American canid.',
      creation: 'Appeared around 2 million years ago.',
      extinction: 'Critically endangered with very few individuals left.',
      image: '30-RED-WOLF-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£300.00'
  },
  {
      id: 31,
      name: 'Zero-Tortoise',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Pinta Island tortoise, a giant tortoise species.',
      creation: 'Appeared around 3 million years ago.',
      extinction: 'Became extinct in 2012.',
      image: '31-TORTOISE-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£310.00'
  },
  {
      id: 32,
      name: 'Zero-Owl',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Spotted owl, a nocturnal bird of prey.',
      creation: 'Appeared around 20 million years ago.',
      extinction: 'Threatened due to habitat loss.',
      image: '32-OWL-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£320.00'
  },
  {
      id: 33,
      name: 'Zero-Manatee',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'West Indian manatee, a large marine mammal.',
      creation: 'Appeared around 50 million years ago.',
      extinction: 'Vulnerable due to habitat destruction and boat strikes.',
      image: '33-MANATEE-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£330.00'
  },
  {
      id: 34,
      name: 'Zero-Gibbon',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Hainan gibbon, a small ape.',
      creation: 'Appeared around 2 million years ago.',
      extinction: 'Critically endangered with fewer than 30 individuals left.',
      image: '34-GIBBON-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£340.00'
  },
  {
      id: 35,
      name: 'Zero-River',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Chinese paddlefish, a large freshwater fish.',
      creation: 'Appeared around 200 million years ago.',
      extinction: 'Declared extinct in 2020.',
      image: '35-RIVER-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£350.00'
  },
  {
      id: 36,
      name: 'Zero-Amur',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Amur leopard, a critically endangered big cat.',
      creation: 'Appeared around 2 million years ago.',
      extinction: 'Fewer than 100 individuals remain.',
      image: '36-AMUR-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£360.00'
  },
  {
      id: 37,
      name: 'Zero-Addax',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Addax, also known as the white antelope.',
      creation: 'Appeared around 2 million years ago.',
      extinction: 'Critically endangered due to habitat loss and hunting.',
      image: '37-ADDAX-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£370.00'
  },
  {
      id: 38,
      name: 'Zero-Tuatara',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Tuatara, a reptile endemic to New Zealand.',
      creation: 'Appeared around 200 million years ago.',
      extinction: 'Endangered due to habitat loss.',
      image: '38-TUATARA-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£380.00'
  },
  {
      id: 39,
      name: 'Zero-Ibis',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Northern bald ibis, a critically endangered bird.',
      creation: 'Appeared around 20 million years ago.',
      extinction: 'Critically endangered due to habitat loss.',
      image: '39-IBIS-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£390.00'
  },
  {
      id: 40,
      name: 'Zero-Elephant',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Sumatran elephant, a critically endangered subspecies.',
      creation: 'Appeared around 3 million years ago.',
      extinction: 'Critically endangered due to habitat loss and poaching.',
      image: '40-ELEPHANT-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£400.00'
  },
  {
      id: 41,
      name: 'Zero-Lemur',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Greater bamboo lemur, a critically endangered primate.',
      creation: 'Appeared around 50 million years ago.',
      extinction: 'Critically endangered due to habitat loss.',
      image: '41-LEMUR-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£410.00'
  },
  {
      id: 42,
      name: 'Zero-Dodo',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'The Dodo was a flightless bird native to Mauritius.',
      creation: 'Appeared in the Pleistocene epoch.',
      extinction: 'Became extinct in the late 17th century.',
      image: '42-DODO-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£420.00'
  },
  {
      id: 43,
      name: 'Zero-Harpy',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Harpy eagle, a powerful raptor.',
      creation: 'Appeared around 20 million years ago.',
      extinction: 'Near threatened due to habitat loss.',
      image: '43-HARPY-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£430.00'
  },
  {
      id: 44,
      name: 'Zero-Wolf',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Ethiopian wolf, a critically endangered canid.',
      creation: 'Appeared around 2 million years ago.',
      extinction: 'Fewer than 500 individuals remain.',
      image: '44-WOLF-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£440.00'
  },
  {
      id: 45,
      name: 'Zero-SunBear',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Sun bear, the smallest bear species.',
      creation: 'Appeared around 5 million years ago.',
      extinction: 'Vulnerable due to habitat loss and poaching.',
      image: '45-SUN-BEAR-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£450.00'
  },
  {
      id: 46,
      name: 'Zero-Orca',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Southern resident orcas, a population of killer whales.',
      creation: 'Appeared around 11 million years ago.',
      extinction: 'Critically endangered due to prey depletion and pollution.',
      image: '46-ORCA-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£460.00'
  },
  {
      id: 47,
      name: 'Zero-Pika',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Ili pika, a small mammal from China.',
      creation: 'Appeared around 20 million years ago.',
      extinction: 'Endangered due to habitat loss.',
      image: '47-PIKA-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£470.00'
  },
  {
      id: 48,
      name: 'Zero-Shark',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Great hammerhead shark, a critically endangered species.',
      creation: 'Appeared around 20 million years ago.',
      extinction: 'Critically endangered due to overfishing.',
      image: '48-SHARK-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£480.00'
  },
  {
      id: 49,
      name: 'Zero-Mynah',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Bali mynah, a critically endangered bird.',
      creation: 'Appeared around 2 million years ago.',
      extinction: 'Critically endangered due to habitat loss and pet trade.',
      image: '49-MYNAH-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£490.00'
  },
  {
      id: 50,
      name: 'Zero-Camel',
      tokenStandard: 'ERC-20 Smart Contract',
      deployment: 'Ethereum Mainnet',
      contractAddress: '0xa47d4023561527a2ec51b216ab06cc5be34f38bd',
      description: 'Wild Bactrian camel, a critically endangered species.',
      creation: 'Appeared around 3 million years ago.',
      extinction: 'Critically endangered due to habitat loss and hunting.',
      image: '50-CAMEL-FRONT-ERC20-ZERO-TOKEN.png',
      price: '£500.00'
  }
];

const CryptoStore = () => {
    const navigate = useNavigate();
    const [scrollDownClicked, setScrollDownClicked] = useState(false);
    const [isBottom, setIsBottom] = useState(false);

    const sectionRefs = useRef([]);
    const scrollDownRef = useRef(null);
    const scrollTopRef = useRef(null);

    const handleMoreDetailsClick = (id) => {
        navigate(`/token/${id}`);
    };

    const handlePurchaseClick = (name) => {
        alert(`Purchasing ${name}`);
    };

    const handleViewOnChainClick = (contractAddress) => {
        window.open(`https://etherscan.io/token/${contractAddress}`, '_blank'); // Updated to Etherscan
    };

    // Refs for GSAP animations
    const tokenRefs = useRef([]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });

        // GSAP Animations for sections
        sectionRefs.current.forEach((section, index) => {
            if (section) {
                gsap.fromTo(
                    section,
                    { opacity: 0, y: 50 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 1,
                        delay: index * 0.2,
                        scrollTrigger: {
                            trigger: section,
                            start: "top 80%",
                        },
                    }
                );
            }
        });

        // GSAP Animation for Scroll Down Button (Initial Animation)
        if (scrollDownRef.current) {
            gsap.fromTo(
                scrollDownRef.current,
                { opacity: 0, y: -20 },
                { opacity: 1, y: 0, duration: 1 }
            );
        }

        // GSAP Animation for Back to Top Button
        if (scrollTopRef.current) {
            gsap.fromTo(
                scrollTopRef.current,
                { opacity: 0, y: 20 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.5,
                    scrollTrigger: {
                        trigger: scrollTopRef.current,
                        start: "top bottom",
                    },
                }
            );
        }
    }, []);

    // Check if the user is near the bottom of the page
    const checkIfBottom = () => {
        if (
            window.innerHeight + window.scrollY >=
            document.body.offsetHeight - 200
        ) {
            setIsBottom(true); // Show Back to Top button
        } else {
            setIsBottom(false); // Hide Back to Top button
        }
    };

    // Scroll to the content section with animation and hide the button
    const scrollToContent = () => {
        const content = document.getElementById("content");
        if (content) {
            const contentOffsetTop = content.offsetTop;
            window.scrollTo({
                top: contentOffsetTop - 100, // Adjust offset as needed
                behavior: "smooth",
            });
        }

        // Animate the Scroll Down button before hiding it
        if (scrollDownRef.current) {
            gsap.to(scrollDownRef.current, {
                opacity: 0,
                y: -20,
                duration: 0.5,
                onComplete: () => setScrollDownClicked(true),
            });
        }
    };

    // Scroll to the top of the page
    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
        setScrollDownClicked(false); // Show Scroll Down button (if needed)
    };

    // Add a scroll listener to monitor page position
    useEffect(() => {
        window.addEventListener("scroll", checkIfBottom);
        return () => {
            window.removeEventListener("scroll", checkIfBottom);
        };
    }, []);

    // GSAP Animations for token items
    useEffect(() => {
        tokenRefs.current.forEach((token, index) => {
            if (token) {
                gsap.fromTo(
                    token,
                    { opacity: 0, y: 50 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 1,
                        delay: index * 0.1,
                        scrollTrigger: {
                            trigger: token,
                            start: "top 80%",
                        },
                    }
                );
            }
        });
    }, []);

    return (
        <div className="w-screen min-h-screen bg-black text-blue-50 flex flex-col items-center relative">
            {/* Hero Section - Full Viewport Height */}
            <div className="relative w-full h-screen flex flex-col items-center justify-center p-5">
                {/* Hero Video Container - Matching ZeroCoin style */}
                <div className="relative w-full max-w-6xl h-64 md:h-[500px] overflow-hidden rounded-[4rem] border border-white/10 z-10 bg-black shadow-[0_0_80px_rgba(34,211,238,0.15)]">
                    <video
                        src="/videos/cryptostore-hero.mp4"
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="absolute left-0 top-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black" />
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <SparklesCore
                            id="cryptoStoreHeroSparkles"
                            background="transparent"
                            minSize={0.4}
                            maxSize={1.0}
                            particleDensity={30}
                            particleColor="#22d3ee"
                            className="absolute inset-0"
                        />
                        <AnimatedTitle
                            title="Zero-Token Store: Powering Your Future"
                            containerClass="text-center text-blue-50 font-zentry relative z-10"
                        />
                        <motion.p 
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5 }}
                            className="text-cyan-400/80 font-bold tracking-[0.4em] uppercase text-[10px] mt-6 z-10"
                        >
                            Premium Assets · Secure Commerce · Direct Access
                        </motion.p>
                    </div>
                </div>
            </div>

            {/* Scroll Down Button - Fixed at bottom center */}
            {!scrollDownClicked && (
                <ScrollDownButton
                    ref={scrollDownRef}
                    additionalClasses=""
                    onClick={scrollToContent}
                />
            )}

            {/* Content Section */}
            <div id="content" className="px-6 md:px-10 py-20 mx-auto max-w-7xl w-full">
                {/* Stats Section */}
                <div
                    className="mb-16 grid grid-cols-2 md:grid-cols-4 gap-6"
                    ref={(el) => (sectionRefs.current[0] = el)}
                >
                    {[
                        { value: '50+', label: 'Unique Tokens', icon: '🦁' },
                        { value: 'ERC-20', label: 'Token Standard', icon: '⛓️' },
                        { value: 'ETH', label: 'Blockchain', icon: '💎' },
                        { value: '£10+', label: 'Starting From', icon: '💰' },
                    ].map((stat, index) => (
                        <div
                            key={index}
                            className="relative group bg-gradient-to-br from-gray-900/80 via-gray-800/50 to-gray-900/80 backdrop-blur-xl p-6 rounded-2xl border border-white/10 overflow-hidden hover:border-cyan-500/30 transition-all duration-500"
                        >
                            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 via-transparent to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                            <div className="absolute -top-10 -right-10 w-24 h-24 bg-cyan-500/10 rounded-full blur-2xl group-hover:bg-cyan-500/20 transition-all duration-500" />
                            <div className="relative z-10 text-center">
                                <span className="text-3xl mb-2 block">{stat.icon}</span>
                                <span className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-white bg-clip-text text-transparent">
                                    {stat.value}
                                </span>
                                <p className="text-gray-200 mt-2 text-sm uppercase tracking-wider">{stat.label}</p>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Introduction with Premium Styling */}
                <div
                    className="mb-16 text-center relative"
                    ref={(el) => (sectionRefs.current[1] = el)}
                >
                    {/* Glow effect behind title */}
                    <div className="absolute left-1/2 top-0 -translate-x-1/2 w-96 h-20 bg-cyan-500/20 blur-3xl" />
                    
                    <h2 className="relative text-4xl md:text-5xl font-bold font-zentry bg-gradient-to-r from-white via-cyan-200 to-white bg-clip-text text-transparent drop-shadow-lg">
                        Digital Conservation Collection
                    </h2>
                    <p className="mt-5 text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed">
                        Each token represents a unique endangered or extinct species, symbolizing our commitment to 
                        conservation through blockchain technology. A portion of every purchase supports wildlife preservation efforts.
                    </p>
                </div>

                {/* Collection Overview with Enhanced Styling */}
                <div
                    className="mb-16 relative"
                    ref={(el) => (sectionRefs.current[2] = el)}
                >
                    {/* Container with animated border */}
                    <div className="relative p-8 md:p-12 rounded-3xl overflow-hidden">
                        {/* Outer glow */}
                        <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500/20 via-purple-500/20 to-pink-500/20 rounded-3xl blur-xl opacity-60" />
                        
                        {/* Card background */}
                        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-850 to-gray-900 rounded-3xl border border-white/10" />
                        
                        <div className="relative grid md:grid-cols-3 gap-8 text-center">
                            <div className="p-4">
                                <div className="text-4xl mb-3">🌍</div>
                                <h3 className="text-xl font-bold text-white mb-2">Conservation Mission</h3>
                                <p className="text-gray-300 text-sm">Raising awareness for endangered and extinct species through blockchain technology.</p>
                            </div>
                            <div className="p-4 border-x border-white/10">
                                <div className="text-4xl mb-3">🔐</div>
                                <h3 className="text-xl font-bold text-white mb-2">Verified On-Chain</h3>
                                <p className="text-gray-300 text-sm">All tokens are ERC-20 smart contracts deployed on Ethereum mainnet.</p>
                            </div>
                            <div className="p-4">
                                <div className="text-4xl mb-3">💎</div>
                                <h3 className="text-xl font-bold text-white mb-2">Collectible Value</h3>
                                <p className="text-gray-300 text-sm">Limited edition tokens with unique artwork representing each species.</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Collection Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
                    <div>
                        <h3 className="text-2xl md:text-3xl font-bold text-white">
                            Token Collection
                        </h3>
                        <p className="text-gray-300 mt-1">
                            {tokens.length} unique species available
                        </p>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                        <span className="px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-full border border-emerald-500/30">
                            🟢 Live on Ethereum
                        </span>
                    </div>
                </div>

                {/* Tokens Grid - Professional 3 Column Layout */}
                <div className="w-full" id="tokens-list">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch auto-rows-fr">
                        {tokens.map((token, index) => (
                            <TokenCard
                                key={token.id}
                                token={token}
                                onMoreDetails={handleMoreDetailsClick}
                                onPurchase={handlePurchaseClick}
                                onViewOnChain={handleViewOnChainClick}
                                ref={(el) => (tokenRefs.current[index] = el)}
                            />
                        ))}
                    </div>
                </div>

                {/* Bottom CTA Section */}
                <div
                    className="mt-20 relative"
                    ref={(el) => (sectionRefs.current[3] = el)}
                >
                    <div className="relative p-10 md:p-16 rounded-3xl overflow-hidden text-center">
                        {/* Background glow */}
                        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 via-purple-500/10 to-pink-500/10 rounded-3xl" />
                        <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500/20 via-purple-500/20 to-pink-500/20 rounded-3xl blur-xl opacity-30" />
                        <div className="absolute inset-0 bg-gray-900/80 rounded-3xl border border-white/10" />
                        
                        <div className="relative z-10">
                            <h3 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-white via-cyan-200 to-white bg-clip-text text-transparent mb-4">
                                Join the Conservation Movement
                            </h3>
                            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
                                Own a piece of natural history and contribute to wildlife preservation. 
                                Each token purchase supports conservation initiatives worldwide.
                            </p>
                            <div className="flex flex-wrap justify-center gap-4">
                                <GlassButton
                                    onClick={() => navigate('/ico')}
                                    className="px-8 py-4"
                                    variant="cyan"
                                    size="lg"
                                >
                                    <div className="flex items-center gap-2">
                                      <FaChartLine /> View ICO Details
                                    </div>
                                </GlassButton>
                                <GlassButton
                                    onClick={() => navigate('/zero-coin')}
                                    className="px-8 py-4"
                                    variant="gray"
                                    size="lg"
                                >
                                    <div className="flex items-center gap-2">
                                      <FaCoins /> Learn About Zero-Coin
                                    </div>
                                </GlassButton>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Back to Top Button */}
            {isBottom && (
                <div
                    ref={scrollTopRef}
                    className="fixed bottom-10 left-5 flex items-center gap-3 px-5 py-3 bg-black/40 backdrop-blur-sm border border-cyan-500/30 rounded-full shadow-lg shadow-cyan-500/10 cursor-pointer hover:bg-black/60 hover:border-cyan-400/50 transition-all duration-500 z-50"
                    onClick={scrollToTop}
                    aria-label="Back to Top"
                >
                    <TiArrowUp
                        className="text-cyan-400 transition-all duration-500"
                        size={24}
                    />
                    <span className="relative z-20 text-sm uppercase text-white/80 font-medium tracking-wider transition-colors duration-500 hover:text-white">
                        BACK TO TOP
                    </span>
                </div>
            )}

            {/* Footer */}
            <Footer />
        </div>
    );
};

export default CryptoStore;